/**
 * Font Replacement Patch for CreateJS Text
 * Enables dynamic font replacement based on language or other criteria
 */
export default class FontReplacementPatch {
    static _fontProvider = null;
    static _fontSizeProvider = null;
    static _lineHeightMultiplierProvider = null;

    /**
     * Set a provider function to determine which font to use
     * @param {Function} provider - Function that returns a font name string (e.g., "Arial") or null to use default
     */
    static setFontProvider(provider) {
        FontReplacementPatch._fontProvider = provider;
    }

    /**
     * Set a provider function to determine font size mappings
     * @param {Function} provider - Function that returns a size mapping object (e.g., {"39": "45"}) or null
     */
    static setFontSizeProvider(provider) {
        FontReplacementPatch._fontSizeProvider = provider;
    }

    /**
     * Set a provider function to determine line-height multiplier
     * @param {Function} provider - Function that returns a number (e.g., 1.2) or null
     */
    static setLineHeightMultiplierProvider(provider) {
        FontReplacementPatch._lineHeightMultiplierProvider = provider;
    }

    /**
     * Get the replacement font for a text object
     * @param {Object} textObj - The text object (optional, for future per-instance font control)
     * @returns {string|null} Font name to use, or null to keep original
     */
    static _getReplacementFont(textObj) {
        const provider = FontReplacementPatch._fontProvider;
        if (typeof provider === 'function') {
            return provider();
        }
        return null;
    }

    /**
     * Get the font size mappings for the current language
     * @returns {Object|null} Size mapping object (e.g., {"39": "45"}) or null
     */
    static _getFontSizeMappings() {
        const provider = FontReplacementPatch._fontSizeProvider;
        if (typeof provider === 'function') {
            return provider();
        }
        return null;
    }

    /**
     * Get the line-height multiplier for the current language/context
     * @param {Object} textObj - The text object (optional, for future per-instance control)
     * @returns {number|null} Multiplier value (e.g., 1.2) or null
     */
    static _getLineHeightMultiplier(textObj) {
        const provider = FontReplacementPatch._lineHeightMultiplierProvider;
        if (typeof provider === 'function') {
            return provider(textObj);
        }
        return null;
    }

    /**
     * Replace font size in a CSS font string
     * @param {string} fontString - Original font string (e.g., "39px 'Poppins'")
     * @param {Object} sizeMappings - Size mapping object (e.g., {"39": "45"})
     * @returns {string} Modified font string with new size (e.g., "45px 'Poppins'")
     */
    static _replaceFontSize(fontString, sizeMappings) {
        if (!sizeMappings) return fontString;
        
        // Extract current font size
        const sizeMatch = fontString.match(/(\d+)px/);
        if (!sizeMatch) return fontString;
        
        const currentSize = sizeMatch[1];
        const newSize = sizeMappings[currentSize];
        
        // Only replace if mapping exists for this size
        if (!newSize) return fontString;
        
        // Replace size value
        return fontString.replace(/(\d+)px/, `${newSize}px`);
    }

    /**
     * Replace font name in a CSS font string
     * @param {string} fontString - Original font string (e.g., "bold 39px 'Poppins Medium'")
     * @param {string} newFontName - New font name to replace with (e.g., "Comic Sans MS")
     * @returns {string} Modified font string with new font name
     */
    static _replaceFontName(fontString, newFontName) {
        // Known font weight/style variants that appear as suffixes
        const variants = ['ExtraBold', 'SemiBold', 'Bold', 'Medium', 'Light', 'Regular', 'Italic', 'Black', 'Thin'];
        
        // Extract font family from quotes: "39px 'Poppins Medium'" -> "Poppins Medium"
        const quotedMatch = fontString.match(/px\s*'([^']+)'/);
        if (!quotedMatch) {
            console.warn('FontReplacementPatch: Could not extract font family from', fontString);
            return fontString;
        }
        
        const oldFullFontName = quotedMatch[1]; // e.g., "Poppins Medium", "Comic Sans MS Bold"
        
        // Check if the font name ends with a known variant
        let variant = '';
        for (const v of variants) {
            if (oldFullFontName.endsWith(v)) {
                variant = v;
                break;
            }
        }
        
        // Construct new font name preserving the variant
        const newFullFontName = variant ? `${newFontName} ${variant}` : newFontName;
        
        
        // Replace old font family with new one (preserves CSS keywords, quotes, etc.)
        return fontString.replace(oldFullFontName, newFullFontName);
    }

    /**
     * Apply font replacement patch to createjs.Text
     */
    static apply() {
        if (typeof createjs === 'undefined' || !createjs.Text) {
            console.warn('FontReplacementPatch: createjs.Text not found');
            return;
        }

        const CurrentTextCtr = createjs.Text;

        // Wrap constructor to replace font before text object creation
        const FontReplacementTextCtr = function(text, font, color) {
            let modifiedFont = font;
            
            // Check if font size replacement is needed
            const fontSizeMappings = FontReplacementPatch._getFontSizeMappings();
            if (fontSizeMappings && font) {
                modifiedFont = FontReplacementPatch._replaceFontSize(modifiedFont, fontSizeMappings);
            }
            
            // Check if font replacement is needed
            const replacementFont = FontReplacementPatch._getReplacementFont(this);
            if (replacementFont && modifiedFont) {
                modifiedFont = FontReplacementPatch._replaceFontName(modifiedFont, replacementFont);
            }
            
            // Call the underlying constructor in the chain
            CurrentTextCtr.call(this, text, modifiedFont, color);
        };

        FontReplacementTextCtr.prototype = CurrentTextCtr.prototype;
        FontReplacementTextCtr.prototype.constructor = FontReplacementTextCtr;

        // Add getter/setter for lineHeight, to override with optional multiplied value
        Object.defineProperty(FontReplacementTextCtr.prototype, 'lineHeight', {
            configurable: true,
            enumerable: true,
            get: function() {
                return this.__fontReplacementLineHeight;
            },
            set: function(value) {
                const multiplier = FontReplacementPatch._getLineHeightMultiplier(this);
                this.__fontReplacementLineHeight = multiplier == null ? value : value * multiplier;
            }
        });

        // Copy static properties
        for (let prop in CurrentTextCtr) {
            if (CurrentTextCtr.hasOwnProperty(prop)) {
                FontReplacementTextCtr[prop] = CurrentTextCtr[prop];
            }
        }

        createjs.Text = FontReplacementTextCtr;
    }
}
